import { GatewayAuthentication } from "@conformite/gateway";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { UserSessionData, userSession } from "./UserSession";

type UserSessionContextValue = UserSessionData & {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  isInitializing: boolean;
  endyaAdmin: boolean;
};
export const UserSessionContext = createContext<UserSessionContextValue>(
  {} as UserSessionContextValue
);

export function UserSessionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [session, _setSession] = useState<UserSessionData>(
    // eslint-disable-next-line @typescript-eslint/unbound-method
    userSession.sessionData
  );
  const [isInitializing, setIsInitializing] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    void userSession.initializationPromise.finally(() => {
      setIsInitializing(false);
    });
  }, []);

  useEffect(() => {
    if (!session.isConnected) queryClient.clear();
  }, [session.isConnected, queryClient]);

  userSession.onSetSessionData = _setSession;

  const value = useMemo(() => {
    return {
      ...session,
      isInitializing,
      endyaAdmin: session.roles.includes(
        GatewayAuthentication.Role.ENDYA_ADMIN
      ),
      login: userSession.login.bind(userSession),
      logout: userSession.logout.bind(userSession),
      refresh: userSession.refresh.bind(userSession),
    };
  }, [session, isInitializing]);

  return (
    <UserSessionContext.Provider value={value}>
      {children}
    </UserSessionContext.Provider>
  );
}

export const useSession = () => {
  return useContext(UserSessionContext);
};
